/**
 * What was decided on Home (admin rebuild step 3), kept so the screen can say it ("Just decided") and take it back.
 * No words are written here (utils/homeWords has them) and nothing here calls the API.
 *
 *  - `tried`: ids of failed social posts that were queued again from Home. The server still counts a failed post for
 *    a day after it is retried (the retry makes a NEW post and leaves the failed one as it was), so without this the
 *    line would come back on the next poll and invite a second press. Saved in this browser, per post id.
 *  - `decided`: the "Just decided" strip, a plain record (no Undo). Lives for 15 minutes, in memory.
 *
 * A small external store, so Home and the sidebar badge read one answer (hooks use useSyncExternalStore).
 */

export const JUST_DECIDED_MS = 15 * 60 * 1000;
const TRIED_KEY = "tsfs.home.triedAgain.v1";
const KEEP_TRIED_MS = 48 * 60 * 60 * 1000; // the server counts a failure for 24 h; two days is plenty

export interface DecidedEntry {
  id: string;
  at: number;
  /** The words shown, already written (utils/homeWords). */
  text: string;
}

type Tried = Record<string, number>;

const readTried = (): Tried => {
  try {
    const raw = JSON.parse(window.localStorage.getItem(TRIED_KEY) || "{}");
    const now = Date.now();
    const out: Tried = {};
    Object.keys(raw && typeof raw === "object" ? raw : {}).forEach((k) => {
      if (typeof raw[k] === "number" && now - raw[k] < KEEP_TRIED_MS) out[k] = raw[k];
    });
    return out;
  } catch (e) {
    return {}; // storage blocked or unreadable: the page works without it
  }
};
const writeTried = (t: Tried) => {
  try {
    window.localStorage.setItem(TRIED_KEY, JSON.stringify(t));
  } catch (e) {
    /* storage blocked: kept in memory for this visit */
  }
};

let tried: Tried = readTried();
let decided: DecidedEntry[] = [];
const subscribers = new Set<() => void>();
const emit = () => subscribers.forEach((f) => f());

export const subscribe = (f: () => void): (() => void) => {
  subscribers.add(f);
  return () => {
    subscribers.delete(f);
  };
};

export const getTried = (): Tried => tried;
export const getDecided = (): DecidedEntry[] => decided;

export const markTried = (failedId: string) => {
  tried = { ...tried, [failedId]: Date.now() };
  writeTried(tried);
  emit();
};
export const pushDecided = (e: Omit<DecidedEntry, "at">) => {
  decided = [{ ...e, at: Date.now() }, ...decided.filter((d) => d.id !== e.id)];
  emit();
};
/** Entries still inside the 15 minutes. */
export const liveDecided = (list: DecidedEntry[], now: number = Date.now()): DecidedEntry[] => list.filter((d) => now - d.at < JUST_DECIDED_MS);

/** Sign-out and tests: forget everything (the next person on this browser starts clean). */
export const resetHomeDecisions = () => {
  tried = {};
  decided = [];
  try {
    window.localStorage.removeItem(TRIED_KEY);
  } catch (e) {
    /* nothing to remove */
  }
  emit();
};
